import React, {
  useRef, useMemo, useCallback, useState,
} from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import IcomoonReact from 'icomoon-react';
import Dropdown from 'react-bootstrap/Dropdown';
import { Picker } from 'emoji-mart';
import { Grid } from '@giphy/react-components';
import { GiphyFetch } from '@giphy/js-fetch-api';

import iconSet from '../../../../shared/images/teambuildr-selection.json';

import useFeed from '../hooks/useFeed';

// Initialize Giphy SDK
const giphyApiKey = process.env.GIPHY_API_KEY || 'sXpGFDGZs0Dv1mmNFvYaGUvYwKX0PWIh'; // Demo key fallback
const gf = new GiphyFetch(giphyApiKey);

const GiphyGridContainer = styled('div')`
  width: 280px;
  height: 350px;
  overflow-y: auto;
  overflow-x: hidden;
`;

const GiphySearchWrapper = styled('div')`
  padding: 10px;
  
  input {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
    margin-bottom: 10px;
    font-family: 'Open Sans', sans-serif;
    
    &:focus {
      outline: none;
      border-color: #0DCC8A;
    }
  }

  div {
    ::-webkit-scrollbar {
      width: 5px;
      :hover {
        overflow-y: scroll;
      }
    }
    ::-webkit-scrollbar-track {
      -webkit-box-shadow: inset 0 0 6px rgba(0,0,0,0.3); 
      border-radius: 10px;
      :hover {
        overflow-y: scroll;
      }
    }
    ::-webkit-scrollbar-thumb {
      border-radius: 10px;
      -webkit-box-shadow: inset 0 0 6px rgba(0,0,0,0.5); 
      :hover {
        overflow-y: scroll;
      }
    }
  }
`;

const DropDownMenuWrapper = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 20%;
  color: #333333;
  &.giphy-dropdown {
    cursor: pointer;
  }
  &.emoji-dropdown {
    cursor: pointer;
  }
  .dropdown-container {
    margin-bottom: 10px;
    border-radius: 5px;
    box-shadow: 0 2px 4px 0 rgba(234, 234, 234, 0.5);
    border: 0px;
    &.giphy {
      padding: 10px;
      z-index: 100;
      max-height: 450px;
      overflow-y: auto;
      
      /* Style Giphy grid */
      img {
        cursor: pointer;
        border-radius: 4px;
      }
    }
    &.emoji {
      padding: 0px;
      z-index: 100;
    }
  }
  .dropdown-item {
    display: flex;
    align-items: center;
    padding: 15px 20px;
    span {
      color: #444444;
    }
    .drop-icon-text {
      margin-left: 10px;
      padding-top: 2px;
      font-weight: 600;
      text-transform: none;
      letter-spacing: 0px;
      font-size: 16px;
    }
    :hover {
      transition: linear 0s;
      background: #006a65;
      color: #ffffff;
      span {
        color: #ffffff;
      }
      svg {
        fill: #ffffff;
      }
    }
  }
  .dropdown-menu {
    padding: 10px 0px;
  }
  .dropdown-divider {
    margin: 0px;
  }
  .add-gif-button {
    height: 32px;
    width: 32px;
    color: #444444;
    background-color: transparent;
    font-weight: 800;
    letter-spacing: -0.5px;
    &:hover {
      opacity: 0.7;
      background-color: transparent;
    }
  }
  &.notification-dropdown {
    position: absolute;
    right: 15px;
  }
  .emoji-picker {
    border: 0;
    z-index: 100;
  }
`;

const MainWrapper = styled('div')`
  .button-wrapper {
    display: flex;
  }
  .animated-div {
    display: flex;
    position: absolute;
    right: 25px;
    z-index: 100;
  }
`;

const SmileyContainer = styled('div')`
  margin-right: 5px;
`;

const AddGifToggle = React.forwardRef(({ children, onClick }, ref) => (
  <SmileyContainer
    href=''
    ref={ref}
    onClick={(e) => {
      e.preventDefault();
      onClick(e);
    }}
    className='dot-box'
    aria-label='Add GIF'
  >
    {children}
    <IcomoonReact
      iconSet={iconSet}
      size={30}
      icon='gif'
    />
  </SmileyContainer>
));

AddGifToggle.propTypes = {
  children: PropTypes.node,
  onClick: PropTypes.func,
};

AddGifToggle.defaultProps = {
  children: null,
  onClick: () => {},
};

const AddEmojiToggle = React.forwardRef(({ children, onClick }, ref) => (
  <SmileyContainer
    href=''
    ref={ref}
    onClick={(e) => {
      e.preventDefault();
      onClick(e);
    }}
    className='dot-box'
    aria-label='Add emoji'
  >
    {children}
    <IcomoonReact
      iconSet={iconSet}
      size={25}
      icon='smiley'
    />
  </SmileyContainer>
));

AddEmojiToggle.propTypes = {
  children: PropTypes.node,
  onClick: PropTypes.func,
};

AddEmojiToggle.defaultProps = {
  children: null,
  onClick: () => {},
};

const GiphyEmojiButtons = ({
  setFieldValue,
  values,
  feedPostId,
  commentId,
  fieldName = 'body',
}) => {
  const { handleUploadMediaUrl } = useFeed();

  const giphyButtonRef = useRef(null);
  const emojiButtonRef = useRef(null);
  const [giphySearchTerm, setGiphySearchTerm] = useState('');

  const handleSelectGif = useCallback((gifUrl) => {
    if (feedPostId || commentId) {
      const activeMediaBarOb = {};
      if (feedPostId) {
        activeMediaBarOb[feedPostId] = true;
      }
      if (commentId) {
        activeMediaBarOb[commentId] = true;
      }
      handleUploadMediaUrl({ mediaUrl: gifUrl }, activeMediaBarOb);
    } else {
      handleUploadMediaUrl({ mediaUrl: gifUrl });
    }
    giphyButtonRef.current.click();
  }, [feedPostId, commentId, handleUploadMediaUrl]);

  const handleSelectEmoji = useCallback((emoji) => {
    const currentValue = values[fieldName] || '';
    const newString = currentValue + emoji.native;
    setFieldValue(fieldName, newString);
    emojiButtonRef.current.click();
  }, [values, fieldName, setFieldValue]);

  const addGiphyDropdown = useMemo(() => (
    <DropDownMenuWrapper className='giphy-dropdown'>
      <Dropdown bsPrefix='trick-fix' id='dropdown-giphy'>
        <Dropdown.Toggle ref={giphyButtonRef} as={AddGifToggle} />
        <Dropdown.Menu className='dropdown-container giphy'>
          <GiphySearchWrapper>
            <input
              type='text'
              placeholder='Search GIFs...'
              value={giphySearchTerm}
              onChange={(e) => setGiphySearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
            <GiphyGridContainer>
              <Grid
                key={giphySearchTerm || 'trending'}
                columns={2}
                width={260}
                fetchGifs={(offset) => {
                  if (giphySearchTerm.trim()) {
                    return gf.search(giphySearchTerm, { offset, limit: 10 });
                  }
                  return gf.trending({ offset, limit: 10 });
                }}
                onGifClick={(gif, e) => {
                  e.preventDefault();
                  handleSelectGif(gif.images.original.url);
                }}
                noLink
              />
            </GiphyGridContainer>
          </GiphySearchWrapper>
        </Dropdown.Menu>
      </Dropdown>
    </DropDownMenuWrapper>
  ), [handleSelectGif, giphyButtonRef, giphySearchTerm]);

  const addEmojiDropdown = useMemo(() => (
    <DropDownMenuWrapper className='emoji-dropdown'>
      <Dropdown bsPrefix='trick-fix'>
        <Dropdown.Toggle ref={emojiButtonRef} as={AddEmojiToggle} />
        <Dropdown.Menu className='dropdown-container emoji'>
          <Picker
            set='apple'
            onSelect={handleSelectEmoji}
            className='emoji-picker'
            title=''
            emoji=''
            sheetSize={32}
          />
        </Dropdown.Menu>
      </Dropdown>
    </DropDownMenuWrapper>
  ), [handleSelectEmoji, emojiButtonRef]);

  const renderButtons = useMemo(() => (
    <>
      {addGiphyDropdown}
      {addEmojiDropdown}
    </>
  ), [addGiphyDropdown, addEmojiDropdown]);

  return (
    <MainWrapper>
      <div className='button-wrapper'>{renderButtons}</div>
    </MainWrapper>
  );
};

GiphyEmojiButtons.propTypes = {
  setFieldValue: PropTypes.func.isRequired,
  values: PropTypes.instanceOf(Object).isRequired,
  feedPostId: PropTypes.number,
  commentId: PropTypes.number,
  fieldName: PropTypes.string.isRequired,
};

GiphyEmojiButtons.defaultProps = {
  feedPostId: null,
  commentId: null,
};

export default GiphyEmojiButtons;
